
import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { TextTools } from 'appshared/text-tools';
import { MiscTools } from 'appshared/misc-tools';

import { AuthService } from 'client/app/services/auth.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';

import { PagingSearchBarComponent } from 'client/app/components/shared/paging-search-bar/paging-search-bar.component';

@Component({
	selector: 'app-table-logins',
	templateUrl: './table-logins.component.html',
	styleUrls: ['./table-logins.component.scss']
})
export class TableLoginsComponent implements OnInit {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;

	@ViewChild(PagingSearchBarComponent) pagingSearchBar: PagingSearchBarComponent = null;

	/*
		pass in
			logs 
			storageKey
			options
			- showUserInfo : true
			- linkUser: false
	
	*/

	authUser: Models.AuthUser = null;
	loading: boolean = false;

	displayOptions: Models.TableDisplayOptions = new Models.TableDisplayOptions();
	pagingOptions: Models.TablePagingOptions = new Models.TablePagingOptions();
	storageKey: string = '';

	showUserInfo: boolean = true;
	linkUser: boolean = false;

	items: Models.LoginHistory[] = [];
	itemsToShow: Models.LoginHistory[] = [];

	columnDefs: Models.ColumnDefinition[] = [
		{
			field: 'login_on',
			type: 'baseDate',
			sortType: 'numeric',
			label: 'Date/Time',
			toolTip: null,
			icon: null
		}, {
			field: 'success',
			type: 'baseText',
			sortType: 'text',
			label: 'Success?',
			toolTip: null,
			icon: null
		}, {
			field: '__username',
			type: 'baseText',
			sortType: 'text',
			label: 'User',
			toolTip: null,
			icon: null
		}, {
			field: '__flag',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'Country',
			icon: 'flag'
		}, {
			field: 'location',
			type: 'baseText',
			sortType: 'text',
			label: 'Location',
			toolTip: null,
			icon: null
		}, {
			field: 'ip',
			type: 'baseText',
			sortType: 'text',
			label: 'IP',
			toolTip: null,
			icon: null
		}, {
			field: '__browser',
			type: 'baseText',
			sortType: 'text',
			label: 'Browser',
			toolTip: null,
			icon: null
		}, {
			field: 'tz_id',
			type: 'baseText',
			sortType: 'text',
			label: 'TZ',
			toolTip: null,
			icon: null
		}
	];

	fieldsToShow: string[] = [];
	allFieldsToShow: string[] = [];
	lockedFields: string[] = [];
	baselineFields: string[] = [];
	minimalFields: string[] = [];

	// ------------------------------------------------------------------------
	constructor(
		private router: Router,
		private uiAlertsService: UiAlertsService,
		private authService: AuthService) {
	}

	// ------------------------------------------------------------------------
	ngOnInit(): void {
		const sessionUser: Models.AuthUser = this.authService.getUser();
		if (sessionUser != null && sessionUser.id !== 0) {
			this.authUser = sessionUser;
			this.setups();
		} // if
	}

	// ------------------------------------------------------------------------
	async setups() {

	}

	// ------------------------------------------------------------------------
	updateContent(items: Models.LoginHistory[], storageKey: string, options: any = {}) {
		this.loading = true;

		this.items = items;
		this.storageKey = storageKey;

		let optionKeys: string[] = [];
		if (options) optionKeys = Object.keys(options);

		if (optionKeys.includes('showUserInfo')) this.showUserInfo = options.showUserInfo;
		if (optionKeys.includes('linkUser')) this.linkUser = options.linkUser;

		this.fieldsToShow = [];
		this.allFieldsToShow = [];
		this.lockedFields = [];
		this.baselineFields = [];
		this.minimalFields = [];

		this.allFieldsToShow.push('login_on');
		this.lockedFields.push('login_on');
		this.allFieldsToShow.push('success');
		if (this.showUserInfo) this.allFieldsToShow.push('__username');
		this.allFieldsToShow.push('__flag');
		this.allFieldsToShow.push('location');
		this.allFieldsToShow.push('ip');
		this.allFieldsToShow.push('__browser');
		this.allFieldsToShow.push('tz_id');

		this.baselineFields = MiscTools.deepClone(this.allFieldsToShow);

		for (const item of this.items) {
			if (item.user_id <= 0)
				item['__browser'] = 'Failed Login';
			else
				item['__browser'] = TextTools.parseUserAgent(item.browser, 'short');
			item['__username'] = TextTools.parseUserInfo(item.user_info);

			const location: string = item.location ? item.location : '';
			const parts: string[] = TextTools.trimList(location.split(','));
			if (parts.length > 1) {
				const country: string = parts[parts.length - 1];
				const prov: string = parts[parts.length - 1];
				item['__flag'] = AppConstants.flagBase + '/' + country.toLowerCase() + '.svg';
			} // if
		} // for

		this.pagingSearchBar.setupPaging(this.items, this.columnDefs, this.storageKey, 'login_on', 'desc', false, this.allFieldsToShow, this.lockedFields, this.baselineFields, this.minimalFields);

		this.loading = false;
	} // updateContent

	// ------------------------------------------------------------------------
	filterItems(matchWords: string[]): any[] {

		const tmpList: Models.LoginHistory[] = [];
		for (const item of this.items) {
			let match: boolean = true;
			if (matchWords.length > 0) {
				let matches: number = 0;
				for (const w of matchWords) {
					if (item['__browser'].toLowerCase().includes(w)
						|| item.tz_id.toLowerCase().includes(w)
						|| item.ip.toLowerCase().includes(w)
						|| (item.location && item.location.toLowerCase().includes(w))
						|| item.success.toLowerCase().includes(w)
						|| (this.showUserInfo && item['__username'].toLowerCase().includes(w))
					)
						matches++;
				} // for
				if (matches !== matchWords.length) match = false;
			} // if

			if (match) tmpList.push(item);
		} // for

		return tmpList;
	} // filterItems

	// ------------------------------------------------------------------------
	setSortBy(field: string) {
		this.pagingSearchBar.setSortBy(field);
	} // setSortBy

	// ------------------------------------------------------------------------
	// Catch calls from the paging/search bar
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			filterItems: (matchWords: string[]) => {
				return this.filterItems(matchWords);
			},
			updateItemsToShow: (itemsToShow: any[], displayOptions: Models.TableDisplayOptions, pagingOptions: Models.TablePagingOptions) => {
				this.itemsToShow = itemsToShow;
				this.displayOptions = displayOptions;
				this.pagingOptions = pagingOptions;
			},
			updateFieldsToShow: (fieldsToShow: string[]) => {
				this.fieldsToShow = fieldsToShow;
			},
			setPageNum: (pageNum: number, uniqueId: string) => {
				if (this.pagingSearchBar) this.pagingSearchBar.setPageNum(pageNum);
			},
			setSortBy: (field: string) => {
				this.setSortBy(field);
			}
		}
	}

}
